import React, { useEffect, useRef, useState } from "react";
import {
    Typography,
    Button,
    Input,
    IconButton,
    Tooltip,
    Avatar
} from "@material-tailwind/react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Popover } from 'react-tiny-popover';
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import {
    MagnifyingGlassIcon,
    XCircleIcon,
    InformationCircleIcon,
    ChatBubbleOvalLeftEllipsisIcon,
    ChevronDownIcon,
    EllipsisVerticalIcon,
    TruckIcon,
    DocumentArrowDownIcon,
    Bars3BottomLeftIcon,
    StarIcon
} from "@heroicons/react/24/solid";
import { approveOrderByFinance, crApproveRejectOrder, fetchAssignedMembers, fetchOrderItems, getOrderById, sendOrderToCRManager, updateVendorAssigmentApprovel } from "@/store/slices/orderSlice";
import useHasPermission from "@/components/useHasPermission";
import OrderChat from "./order-chat";
import OrderCustomerInfo from "./order-customer-info";
import OrderSummary from "./order-summary";
import OrderRefundRequests from "./order-refund-requests";
import OrderPayments from "./order-payments";
import Toaster from "@/components/Toaster";
import DocumentViewModal from "@/components/DocumentViewModal";
import OrderRfq from "./order-rfq";
import Swal from "sweetalert2";
import OrderDeadlinePopover from "./order-deadline-popover";
import ViewAssignedMembers from "./order-modals/view-assigned-members";
import ViewVendorVarientPriceModal from "./order-modals/view-vendor-varient-price-modal";
import ViewDeliveryTimelineRouteModal from "./order-modals/view-delivery-timeline-route-modal";
import ViewUploadedProofModal from "./order-modals/view-uploaded-proof-modal";
import ViewUploadedPickupProofModal from "./order-modals/view-uploaded-pickup-proof-modal";
import ViewAssignedDeliveryAgentModal from "./order-modals/view-assigned-delivery-agent-modal";
import OrderFeedback from "./order-feedback";
import { useTranslation } from "react-i18next";

let action_options = [];
export function CompletedOrderItems() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { t } = useTranslation('order');
    const { t: ct } = useTranslation('common');

    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const rfq_id = searchParams.get("rfq_id");

    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState("orders");
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isActionPopoverOpen, setIsActionPopoverOpen] = useState(false);
    const [isDelivaryPopoverOpen, setIsDelivaryPopoverOpen] = useState(false);

    const [isSetPOFile, setPOFile] = useState(false);
    const [filter, setFilter] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [openViewModal, setOpenViewModal] = useState(false);

    const [actionMenuOptions, setActionMenuOptions] = useState([]);
    const [isMemberShow, setIsMemberShow] = useState(false);

    const hasSummaryPermission = useHasPermission("order.view");
    const hasUpdatePermission = useHasPermission(`order.update`);
    const hasViewVendorPermission = useHasPermission(`assign-vendor.view`);
    const hasEditVendorPermission = useHasPermission(`assign-vendor.add`) || useHasPermission(`assign-vendor.update`);
    const hasViewDPPermission = useHasPermission(`assign-delivery-partner.view`);
    const hasEditDPPermission = useHasPermission(`assign-delivery-partner.add`) || useHasPermission(`assign-delivery-partner.update`);

    const { user } = useSelector((state) => state.user);
    const { order_details, order_items, assigned_members, loading, list_loading } = useSelector((state) => state.order);
    const groups = assigned_members?.groups || [];

    // Prefer rfq_id from URL, fallback to order_details once loaded.
    const effectiveRfqId = rfq_id || order_details?.rfq_id;
    // Prevent duplicate fetch calls and stale rfq_id usage.
    const lastFetchKeyRef = useRef("");
    const latestQueryRef = useRef({ rfqId: null, search: "" });

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [openAssignModal, setOpenAssignModal] = useState(false);

    const [isDeliveryTimelinePopoverOpen, setIsDeliveryTimelinePopoverOpen] = useState({});
    const [deliveryTimelineModalOpen, setDeliveryTimelineModalOpen] = useState(false);
    const [deliveryTimelineContext, setDeliveryTimelineContext] = useState(null);
    const [isOpenViewUploadedProof, setIsOpenViewUploadedProof] = useState(false);
    const [isEditCommissionModalOpen, setIsEditCommissionModalOpen] = useState(false);
    const [isAssignMultipleItemToDPModalOpen, setIsAssignMultipleItemToDPModalOpen] = useState(false);
    const [isOpenViewUploadedPickupProof, setIsOpenViewUploadedPickupProof] = useState(false);



    useEffect(() => {
        if (id) dispatch(getOrderById(id));
    }, [id, dispatch]);

    useEffect(() => {
        if (!effectiveRfqId || !id) return;

        const key = `${id}:${effectiveRfqId}`;
        if (lastFetchKeyRef.current === key) return;
        lastFetchKeyRef.current = key;

        dispatch(fetchOrderItems({ rfq_id: effectiveRfqId, search: '' }));

        dispatch(fetchAssignedMembers({
            order_id: id,
            rfq_id: effectiveRfqId,
            search: ''
        }));

    }, [effectiveRfqId, id, dispatch]);

    useEffect(() => {
        latestQueryRef.current = { rfqId: effectiveRfqId, search };
    }, [effectiveRfqId, search]);

    useEffect(() => {
        const { rfqId, search: s } = latestQueryRef.current || {};
        if (!rfqId) return;
        dispatch(fetchOrderItems({ rfq_id: rfqId, search: s || '', sort_order: filter }));
    }, [filter, dispatch]);

    const handleSearch = (event) => {
        if (event.key === "Enter" && search) {
            if (!effectiveRfqId) return;
            dispatch(fetchOrderItems({ rfq_id: effectiveRfqId, search: search }));
        } else if (event.key === "Backspace" && search.length === 0) {
            if (!effectiveRfqId) return;
            dispatch(fetchOrderItems({ rfq_id: effectiveRfqId, search: '' }));
        }
    };

    const handleSearchClick = () => {
        if (!effectiveRfqId) return;
        dispatch(fetchOrderItems({ rfq_id: effectiveRfqId, search: search }));
    };

    const handleRemoveSearch = () => {
        setSearch('');
        if (!effectiveRfqId) return;
        dispatch(fetchOrderItems({ rfq_id: effectiveRfqId, search: '' }));
    };


    const toggleActionPopover = (itemId) => {
        setIsActionPopoverOpen(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };
    const toggleDelivaryPopover = (itemId) => {
        setIsDelivaryPopoverOpen(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const toggleDeliveryTimelinePopover = (itemId) => {
        setIsDeliveryTimelinePopoverOpen(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    // const totalItems = (order_items ?? []).reduce((sum, item) => sum + (item?.quantity ?? 0), 0);
    const totalItems = order_items?.length || 0;

    const [dpModal, setDpModal] = useState(false);
    const [selectedDelivaryPartaner, setselectedDelivaryPartaner] = useState(null);
    const [viewDpContext, setViewDpContext] = useState(null);

    const assignOrderItemToDeliveryAgent = (item, vendor, delivery_partner) => {
        if (vendor?.is_admin_approved === 'pending' || vendor?.is_admin_approved === 'rejected') {
            Toaster.error("Vendor status has been rejected or pending by the admin. Please assign a different vendor for approval.");
            setIsDelivaryPopoverOpen(false);
            return;
        }
        setselectedDelivaryPartaner(delivery_partner);
        setSelectedProduct(item);
        setDpModal(vendor);
    };

    const viewAssignedDeliveryAgent = (item, vendor, delivery_partner) => {
        setViewDpContext({
            itemId: order_details?.id,
            rfq_product_id: item?.rfq_product_id,
            vendor,
            delivery_partner,
            customer: order_details?.customer,
        });
    };

    const openDeliveryTimeline = (item, vendor, delivery_partner) => {
        setDeliveryTimelineContext({
            orderId: id,
            vendor,
            deliveryPartner: delivery_partner,
        });
        setDeliveryTimelineModalOpen(true);
    };

    useEffect(() => {
        let options = [];

        if (hasEditDPPermission) {
            action_options = [{ label: 'Assign Delivery Agent', value: 'assign_delivery_agent' }];
        }

        const crApproveOptions = [
            { label: "Reject", value: "rejected" },
            { label: "Approve", value: "approved" },
        ];
        if (
            user?.roles[0] === "City Representative" && user?.parent_id == null &&
            order_details?.is_send_to_city_rep != null && order_details?.city_representative_approval == null
        ) {
            crApproveOptions.forEach((option) => {
                if (!options.some((o) => o.value === option.value)) {
                    options.unshift(option);
                }
            });
        }
        const crOptions2 = { label: "Send To Manager", value: "sendToManager" };
        if (user?.roles[0] === 'City Representative' && user?.parent_id != null && order_details?.is_send_to_city_rep == null) {
            if (!options.some(o => o.value === 'sendToManager')) {
                options.unshift(crOptions2);
            }
        }

        const financeOption = { label: "Send To Admin", value: "sendToAdmin" };
        if (user?.roles[0] === 'Finance' && user?.parent_id == null && order_details?.finance_approval == 'no') {
            if (!options.some(o => o.value === 'sendToAdmin')) {
                options.unshift(financeOption);
            }
        }
        const adminOptions2 = [
            { label: "Commission & Deadline", value: "edit_commission" },
        ];
        if ((user?.roles[0] === "Sub Admin" || user?.roles[0] === "Administrator") && order_details?.assigned_details?.length > 0) {
            adminOptions2.forEach((option) => {
                if (!options.some((o) => o.value === option.value)) {
                    options.unshift(option);
                }
            });
        }

        setActionMenuOptions(options);

    }, [dispatch, order_details]);

    const editVendor = (item) => {
        setOpenModal(item);
        setIsActionPopoverOpen([]);
    }
    const viewVendor = (item) => {
        setOpenViewModal(item);
        setIsActionPopoverOpen([]);
    }

    const orderSendToCRManager = () => {
        if (order_details?.is_send_to_city_rep !== null && order_details?.city_representative_approval === 'no') {
            Toaster.error('This order has already been submitted to the Manager and is awaiting approval.');
            return;
        }
        if (order_details?.is_send_to_city_rep !== null && order_details?.city_representative_approval === 'yes') {
            Toaster.error('Order has already been approved by Manager.');
            return;
        }
        Swal.fire({
            title: "Are you sure?",
            text: 'Do you really want to send this order to the Manager?',
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes send it!"
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(sendOrderToCRManager({ order_id: order_details?.id })).unwrap().then((res) => {
                    if (res?.status) {
                        Toaster.success(`Order sent to admin successfully!`);
                        dispatch(getOrderById(id));
                    } else {
                        Toaster.error(res?.message);
                    }
                }).catch((error) => {
                    console.error("Sending failed:", error);
                    Toaster.error("Sending failed, please try again.");
                });
            }
        });
    }

    const approveRejectOrder = async (value) => {
        if (order_details?.city_representative_approval === 'no' && value === 'no') {
            Toaster.error('This order has already been rejected.');
            return;
        }
        if (order_details?.city_representative_approval === 'yes' && value === 'yes') {
            Toaster.error('This order has already been approved.');
            return;
        }

        var msg = value == 'yes' ? 'approve' : value == 'no' ? 'reject' : value;
        let text = `Do you want to ${msg} this order?`;

        Swal.fire({
            title: "Are you sure?",
            text: `${text}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes"
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(crApproveRejectOrder({ order_id: order_details?.id, approval: value })).unwrap().then((res) => {
                    if (res?.status) {
                        dispatch(getOrderById(id));
                        let msg = value == 'yes' ? 'approved' : value == 'no' ? 'rejected' : value;
                        Toaster.success(`Order ${msg} successfully!`);
                    } else {
                        Toaster.error(res?.message);
                    }
                }).catch((error) => {
                    console.error("Changes failed:", error);
                    Toaster.error("Changes failed, please try again.");
                });
            }
        });
    };


    const financeApproveOrder = async () => {
        Swal.fire({
            title: "Are you sure?",
            text: `You want to approve this order and forward it to the admin for further processing?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes"
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(approveOrderByFinance({ order_id: order_details?.id, approval: 'yes' })).unwrap().then((res) => {
                    if (res?.status) {
                        Toaster.success(`Order approved and send to admin successfully!`);
                        dispatch(getOrderById(id));
                        navigate('/dashboard/orders');
                    } else {
                        Toaster.error(res?.message);
                    }
                }).catch((error) => {
                    console.error("Changes failed:", error);
                    Toaster.error("Changes failed, please try again.");
                });
            }
        });
    };

    const priorityColorMap = {
        high: "bg-red-500",
        medium: "bg-yellow-500",
        low: "bg-green-500",
    };

    const approveRejectVendorAssigment = async (vendor, type) => {
        const id = vendor?.rfq_product_vendor_id;

        if (!id) {
            Toaster.error("Invalid vendor selection.");
            return;
        }
        try {
            let reason = "";
            let confirm = null;
            //  If rejected → ask for reason
            if (type === "rejected") {
                confirm = await Swal.fire({
                    title: "Reject Vendor",
                    input: "textarea",
                    inputLabel: "Enter rejection reason",
                    inputPlaceholder: "Type your reason here...",
                    inputAttributes: {
                        "aria-label": "Type your reason here"
                    },
                    showCancelButton: true,
                    confirmButtonText: "Submit",
                    cancelButtonText: "Cancel",
                    inputValidator: (value) => {
                        if (!value) {
                            return "Rejection reason is required!";
                        }
                        if (value.trim().length < 3) {
                            return "Reason must be at least 3 characters.";
                        }
                        if (value.trim().length > 300) {
                            return "Reason cannot exceed 300I want to characters.";
                        }
                    }
                });
                reason = confirm.value;
            } else {
                //  Confirm before API call (optional but good UX)
                confirm = await Swal.fire({
                    title: `Are you sure?`,
                    text:
                        type === "rejected"
                            ? "You are about to reject this vendor."
                            : "You are about to approve this vendor.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, proceed",
                });
            }


            if (!confirm.isConfirmed) return;

            const payload = {
                id,
                is_admin_approved: type,
                ...(type === "rejected" && { reason }),
            };

            dispatch(updateVendorAssigmentApprovel(payload)).unwrap().then(res => {
                if (res?.status) {
                    let text = type === "rejected" ? "Vendor rejected successfully." : "Vendor approved successfully.";
                    Toaster.success(text);
                    dispatch(fetchOrderItems({ rfq_id: effectiveRfqId, search: search }));
                } else {
                    Toaster.error(res?.message || "Something went wrong. Please try again.");
                }
            });


        } catch (error) {
            Toaster.error("Something went wrong. Please try again.");
        }
    };

    const [isCheckedItemPopoverOpen, setIsCheckedItemPopoverOpen] = useState(false);
    const [checkedItems, setCheckedItems] = useState([]);
    const handleSingleCheck = (id) => {
        setCheckedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const handleCheckAll = (e) => {
        if (e.target.checked) {
            const filteredItems = order_items
                ?.filter(
                    (row) =>
                        (!row?.delivery_partners || row.delivery_partners.length === 0) &&
                        (row?.vendors && row.vendors.length > 0)
                )
                .map((row) => row.rfq_product_id) || [];

            setCheckedItems(filteredItems);
        } else {
            setCheckedItems([]);
        }
    };

    const validItems =
        order_items?.filter(
            (row) =>
                (!row?.delivery_partners || row.delivery_partners.length === 0) &&
                (row?.vendors && row.vendors.length > 0)
        ) || [];

    const isAllChecked =
        validItems.length > 0 &&
        validItems.every((item) => checkedItems.includes(item.rfq_product_id));

    return (
        <div className="mt-5 mb-8 flex flex-col gap-6">
            {loading && <ShowLoader />}
            <ScrollToTop />

            {/* Header Section */}
            <div className="flex flex-col gap-4 px-4 md:px-6">
                {/* Top Row: Order ID, PO Number, Search */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    {/* Left: Back + Order ID + CR Badge, then PO Number below */}
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-3">
                            <Link
                                onClick={() => navigate(-1)}
                                // to={'/dashboard/orders'} 
                                className="flex items-center text-blue-600 hover:text-blue-800 transition shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </Link>
                            {order_details?.assigned_details?.[0]?.priority && (
                                <span
                                    className={`w-2 h-2 rounded-full ${priorityColorMap[
                                        order_details.assigned_details[0].priority
                                    ]
                                        }`}
                                ></span>
                            )}
                            <Typography variant="h5" color="blue-gray" className="font-bold shrink-0">
                                {t('po_id_label')}: {order_details?.order_id}
                            </Typography>
                            {/* {user?.parent_id !== null && order_details?.assigned_details != null && (order_details?.city_representative_approval != null || order_details?.is_send_to_city_rep != null) && (
                                <span
                                    className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold shrink-0
                                    ${order_details?.city_representative_approval === 'yes'
                                            ? "bg-green-100 text-green-700"
                                            : order_details?.city_representative_approval === "no"
                                                ? "bg-red-100 text-red-700"
                                                : "bg-yellow-100 text-yellow-700"
                                        }`}
                                >
                                    {order_details?.city_representative_approval === 'yes'
                                        ? t('approved_by_manager')
                                        : order_details?.city_representative_approval === "no"
                                            ? t('rejected_by_manager')
                                            : t('pending_for_manager_approval')}
                                </span>
                            )} */}
                        </div>
                        {order_details?.purchase_order_number && (
                            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg shrink-0 w-fit">
                                {order_details?.purchase_order_file && (
                                    <DocumentArrowDownIcon
                                        className="h-4 w-4 text-gray-500 cursor-pointer hover:text-blue-600"
                                        onClick={() => setPOFile(order_details.purchase_order_file)}
                                    />
                                )}
                                <span className="text-sm font-medium text-gray-700">{t('po_label')}: {order_details.purchase_order_number}</span>
                            </div>
                        )}
                    </div>

                    {/* Right: Search + Status, then Action on new line */}
                    <div className="w-full md:w-auto flex flex-col gap-3 md:ml-auto min-w-0">
                        {/* Row 1: Search + Status + Deadline */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                            {order_details?.delivery_status === 'cancelled' && <span
                                className={`text-[10px] px-3 py-2 rounded-full text-center font-bold bg-red-100 text-red-700`}
                            >
                                {order_details?.delivery_status?.charAt(0).toUpperCase() + order_details?.delivery_status?.slice(1)}

                            </span>}
                            {activeTab === "orders" && <div className="w-full sm:w-auto flex items-center gap-2">
                                <div className="w-full sm:w-64 relative">
                                    <Input
                                        label={ct("search")}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyUp={handleSearch}
                                        icon={!search && <MagnifyingGlassIcon className="h-5 w-5" />}
                                        className={search ? 'pr-10' : ''}
                                    />
                                    {search && (
                                        <IconButton
                                            variant="text"
                                            size="sm"
                                            className="!absolute right-1 top-1"
                                            onClick={handleRemoveSearch}
                                        >
                                            <XCircleIcon className="h-5 w-5 text-red-500" />
                                        </IconButton>
                                    )}
                                </div>
                                {search && (
                                    <Button
                                        variant="outlined"
                                        className="flex items-center border-gray-300 capitalize font-bold bg-black text-white whitespace-nowrap"
                                        onClick={handleSearchClick}
                                    >
                                        {t('search_button')}
                                    </Button>
                                )}
                            </div>}
                            <div className="flex items-center gap-3">
                                {order_details &&
                                    order_details?.assigned_details !== null &&
                                    user?.roles?.[0] === 'City Representative' && (
                                        <OrderDeadlinePopover order_data={order_details} />
                                    )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Tabs Section */}
                <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                    <button
                        onClick={() => setActiveTab("orders")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${activeTab === "orders"
                            ? "bg-black text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        <TruckIcon className="h-4 w-4" />
                        {t('order_items_tab')}
                    </button>
                    <button
                        onClick={() => setActiveTab("chat")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${activeTab === "chat"
                            ? "bg-black text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4" />
                        {t('chat_tab')}
                    </button>
                    <button
                        onClick={() => setActiveTab("customer")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${activeTab === "customer"
                            ? "bg-black text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        <InformationCircleIcon className="h-4 w-4" />
                        {t('customer_info_tab')}
                    </button>
                    <button
                        onClick={() => setActiveTab("payments")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${activeTab === "payments"
                            ? "bg-black text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        <InformationCircleIcon className="h-4 w-4" />
                        {t('payments_tab')}
                    </button>
                    <button
                        onClick={() => setActiveTab("rfqs")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${activeTab === "rfqs"
                            ? "bg-black text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        <InformationCircleIcon className="h-4 w-4" />
                        {t('purchase_order_tab')}
                    </button>
                    <button
                        onClick={() => setActiveTab("refund")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${activeTab === "refund"
                            ? "bg-black text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        <InformationCircleIcon className="h-4 w-4" />
                        {t('refund_request_tab')}
                    </button>
                    <button
                        onClick={() => setActiveTab("feedback")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium ${activeTab === "feedback" ? "bg-black text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                    >
                        <StarIcon className="h-4 w-4" />
                        {t('feedback_tab')}
                    </button>
                </div>

                {/* Team member avatars (right aligned like Figma) */}
                <div className="flex justify-end">
                    <div className="flex -space-x-2 cursor-pointer" onClick={() => setIsMemberShow(true)}>
                        {groups?.map((group, index) => (
                            group?.members?.map((row, i) => (
                                <Tooltip key={index} content={t('team_members_tooltip')}>
                                    <Avatar
                                        src={`${row?.assigned_to?.profile_image}` || "/img/default-avatar.png"}
                                        alt={`${row?.assigned_to?.name}`}
                                        size="sm"
                                        className="border-2 border-white"
                                    />
                                </Tooltip>
                            ))
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-4 md:px-6 w-full">
                {activeTab === "orders" ? (
                    <div className="w-full">
                        {/* Controls Bar - Order Items */}
                        <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                            <div className="flex items-center gap-4">

                                <Typography variant="h6" color="blue-gray" className="font-bold">
                                    {t('track_items_title', { count: totalItems })}
                                </Typography>
                            </div>

                            <div className="flex items-center gap-3 flex-wrap">
                                <Popover
                                    isOpen={isFilterPopoverOpen}
                                    positions={["bottom"]}
                                    onClickOutside={() => setIsFilterPopoverOpen(false)}
                                    content={
                                        <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                                            <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2" onClick={() => { setFilter(''); setIsFilterPopoverOpen(false); }}>{t('latest_sort')}</button>
                                            <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2" onClick={() => { setFilter('asc'); setIsFilterPopoverOpen(false); }}>{t('sort_ascending')}</button>
                                            <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2" onClick={() => { setFilter('desc'); setIsFilterPopoverOpen(false); }} >{t('sort_descending')}</button>
                                        </div>
                                    }
                                >
                                    <Button
                                        onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
                                        variant="outlined"
                                        className="flex items-center gap-2 border-gray-300 capitalize"
                                    >
                                        <Bars3BottomLeftIcon className="h-4 w-4" />
                                        {filter ? `(${filter})` : ''}
                                    </Button>
                                </Popover>

                            </div>
                        </div>

                        {/* Items Table - same width as Controls Bar */}
                        <div className="w-full overflow-x-auto rounded-lg mx-0">
                            <table className="w-full min-w-[800px]">
                                <thead>
                                    <tr className="border-b border-gray-300">

                                        <th className="text-left py-3 px-4 first:pl-0 last:pr-0 text-xs font-bold text-gray-700 w-[300px]">{t('product_header')}</th>
                                        <th className="text-left py-3 px-4 first:pl-0 last:pr-0 text-xs font-bold text-gray-700">{t('quantity_header')}</th>
                                        <th className="text-left py-3 px-4 first:pl-0 last:pr-0 text-xs font-bold text-gray-700">{t('installation_header')}</th>
                                        <th className="text-left py-3 px-4 first:pl-0 last:pr-0 text-xs font-bold text-gray-700">{t('unit_price_header')}</th>
                                        <th className="text-left py-3 px-4 first:pl-0 last:pr-0 text-xs font-bold text-gray-700">{t('vendor_header')}</th>
                                        <th className="text-left py-3 px-4 first:pl-0 last:pr-0 text-xs font-bold text-gray-700">{t('delivery_agent_header')}</th>
                                        <th className="text-left py-3 px-4 first:pl-0 last:pr-0 text-xs font-bold text-gray-700"></th>

                                    </tr>
                                </thead>
                                <tbody>
                                    {list_loading ? <tr><td colSpan={6} className="text-center"> <div role="status">
                                        <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                        </svg>
                                    </div></td></tr> : ''}

                                    {!list_loading && order_items?.length === 0 && (
                                        <tr><td colSpan={6} className="text-center py-10 text-blue-gray-500">{t('no_data_found')}</td></tr>
                                    )}

                                    {order_items?.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <tr className="border-b border-gray-300 hover:bg-gray-50 transition">

                                                <td className="py-4 px-4 first:pl-0 last:pr-0 align-top">
                                                    <div className="flex items-start gap-3">

                                                        <Avatar
                                                            src={item.product_image}
                                                            alt={item.product_name}
                                                            size="md"
                                                            variant="rounded"
                                                            className="border border-gray-200 shrink-0"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <Typography variant="small" className="font-bold text-gray-900">
                                                                {item.product_name}
                                                            </Typography>
                                                            <Typography variant="small" className="text-gray-500">
                                                                {t('sku_label')} {item.sku_number}
                                                            </Typography>

                                                        </div>
                                                    </div>
                                                </td>
                                                {/*Quantity */}
                                                <td className="py-4 px-4 first:pl-0 last:pr-0">
                                                    <Typography variant="small" className="font-medium text-gray-900">
                                                        {item.quantity}
                                                    </Typography>
                                                </td>

                                                {/* Installation Checkbox */}
                                                <td className="py-4 px-4 first:pl-0 last:pr-0">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 accent-[#000000] cursor-pointer"
                                                        checked={item?.is_installation}
                                                        readOnly
                                                    />
                                                </td>

                                                {/* Unit Price */}
                                                <td className="py-4 px-4 first:pl-0 last:pr-0">
                                                    <Typography variant="small" className="font-medium text-gray-900">
                                                        {t('sar_currency_label')} {item.base_price}
                                                    </Typography>
                                                </td>

                                                {/* Vendor Column */}
                                                <td className="py-4 px-4 first:pl-0 last:pr-0">
                                                    {item?.vendors?.map((vendor, vIdx) => (
                                                        <div key={vendor?.rfq_product_vendor_id ?? vIdx} className="flex items-center gap-1 mb-2 last:mb-0">
                                                            {/* 1. Vendor Name */}
                                                            <Typography variant="small" className="text-gray-900">
                                                                {vendor.vendor_name}
                                                            </Typography>

                                                            {/* 2. Info Tooltip (Details) */}
                                                            {vendor.price && (
                                                                <Tooltip
                                                                    placement="top"
                                                                    interactive={true}
                                                                    className="bg-white text-gray-800 border border-gray-200 shadow-xl rounded-lg px-6 py-4 min-w-[320px]"
                                                                    content={
                                                                        <div className="text-sm space-y-3">
                                                                            <Typography variant="small" className="font-bold text-blue-gray-900 border-b border-gray-200 pb-2 text-center capitalize">
                                                                                {t('vendor_details_title')}
                                                                            </Typography>
                                                                            <div className="space-y-2.5">
                                                                                <div className="flex items-start">
                                                                                    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 w-28 shrink-0">{t('vendor_name_label')}:</span>
                                                                                    <span className="font-bold text-gray-900 leading-tight">{vendor.vendor_name}</span>
                                                                                </div>
                                                                                <div className="flex items-center">
                                                                                    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 w-28 shrink-0">{t('email_label')}:</span>
                                                                                    <span className="text-gray-700 font-medium truncate">{vendor.email}</span>
                                                                                </div>
                                                                                <hr className="border-gray-100" />
                                                                                <div className="flex flex-col gap-2">
                                                                                    <div className="flex items-center">
                                                                                        <span className="font-medium text-gray-600 w-28 shrink-0">{t('quantity_label')}:</span>
                                                                                        <span className="text-gray-900 font-bold">{vendor.product_quantity}</span>
                                                                                    </div>
                                                                                    <div className="flex items-center">
                                                                                        <span className="font-medium text-gray-600 w-28 shrink-0">{t('unit_price_label')}:</span>
                                                                                        <span className="text-green-600 font-bold">{t('sar_currency_label')} {vendor.price}</span>
                                                                                    </div>
                                                                                    <div className="flex items-start">
                                                                                        <span className="font-medium text-gray-600 w-28 shrink-0">{t('pickup_label')}:</span>
                                                                                        <span className="text-gray-800 font-medium">{vendor?.pickup_location}</span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            {vendor.vendor_file && (
                                                                                <div className="pt-3 mt-1 border-t border-gray-200">
                                                                                    <a href={vendor.vendor_file} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition font-bold text-xs">
                                                                                        <DocumentArrowDownIcon className="h-4 w-4" />
                                                                                        {t('download_vendor_file')}
                                                                                    </a>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    }
                                                                >
                                                                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 hover:bg-gray-200 transition cursor-pointer">
                                                                        <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                                                                    </div>
                                                                </Tooltip>
                                                            )}

                                                            {/* 3. Three-Dot Action Menu (Popover) */}
                                                            <Popover
                                                                isOpen={isDelivaryPopoverOpen[vendor?.rfq_product_vendor_id]}
                                                                positions={["bottom", "left"]}
                                                                padding={4}
                                                                onClickOutside={() => toggleDelivaryPopover(vendor?.rfq_product_vendor_id)}
                                                                content={
                                                                    <div className="bg-white shadow-xl rounded-md w-40 flex flex-col z-50 border border-gray-100 py-1">
                                                                        {hasViewDPPermission && (
                                                                            <button
                                                                                className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                                onClick={() => viewAssignedDeliveryAgent(item, vendor, item?.delivery_partners[vIdx])}
                                                                            >
                                                                                {t('view_assigned_dp')}
                                                                            </button>
                                                                        )}
                                                                        {((user.roles[0] == 'Administrator' || user.roles[0] == 'Sub Admin') && vendor?.is_admin_approved === 'pending') && (
                                                                            <>
                                                                                <button
                                                                                    className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                                    onClick={() => approveRejectVendorAssigment(vendor, 'approved')}
                                                                                >
                                                                                    {t('approve_vendor_assignment')}
                                                                                </button>
                                                                                <button
                                                                                    className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                                    onClick={() => approveRejectVendorAssigment(vendor, 'rejected')}
                                                                                >
                                                                                    {t('reject_vendor_assignment')}
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                }
                                                            >
                                                                <IconButton
                                                                    variant="text"
                                                                    size="sm"
                                                                    onClick={() => toggleDelivaryPopover(vendor?.rfq_product_vendor_id)}
                                                                >
                                                                    <EllipsisVerticalIcon className="h-5 w-5 text-gray-600" />
                                                                </IconButton>
                                                            </Popover>

                                                            {/* 4. Status Badges */}
                                                            {vendor?.is_admin_approved === 'pending' && (
                                                                <span className="w-auto min-w-[110px] text-[11px] px-3 py-1 rounded-full text-center font-semibold capitalize bg-yellow-100 text-yellow-700 ml-auto ">
                                                                    {t('admin_approval_pending')}
                                                                </span>
                                                            )}
                                                            {vendor?.is_admin_approved === 'rejected' && (
                                                                <span className="w-auto min-w-[110px] text-[11px] px-3 py-1 rounded-full text-center font-semibold capitalize bg-red-100 text-red-700 ml-auto">
                                                                    {t('rejected_by_admin')}
                                                                </span>
                                                            )}
                                                            {vendor?.is_admin_approved === 'approved' && (
                                                                <span className="w-auto min-w-[110px] text-[11px] px-3 py-1 rounded-full text-center font-semibold capitalize bg-green-100 text-green-700 ml-auto">
                                                                    {t('approved_by_admin')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </td>

                                                {/* Delivery Agent */}
                                                <td className="py-4 px-4 first:pl-0 last:pr-0">
                                                    {item?.delivery_partners?.map((delivery_partner, dIdx) => (
                                                        <div key={delivery_partner?.id ?? dIdx} className="flex items-center gap-1">
                                                            <Typography variant="small" className="text-gray-900">
                                                                {delivery_partner.full_name}
                                                            </Typography>
                                                            <Tooltip
                                                                placement="top"
                                                                className="bg-white text-gray-800 border border-gray-200 shadow-lg rounded-lg px-3 py-2"
                                                                content={
                                                                    <div className="text-sm space-y-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium text-gray-600">{t('qty_label')}:</span>
                                                                            <span className="text-gray-900">{delivery_partner?.quantity}</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium text-gray-600">{t('delivery_charge_label')}:</span>
                                                                            <span className="text-green-600 font-semibold">
                                                                                {t('sar_currency_label')} {delivery_partner?.delivery_charge}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium text-gray-600">{t('delivery_info_label')}:</span>
                                                                            <span className="text-green-600 font-semibold">
                                                                                {delivery_partner?.delivery_address}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                }
                                                            >
                                                                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 hover:bg-gray-200 transition cursor-pointer">
                                                                    <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                                                                </div>
                                                            </Tooltip>



                                                            {/* Wrap the Popover in a condition: Only show if status is NOT rejected */}
                                                            {delivery_partner?.status !== 'rejected' && (
                                                                <Popover
                                                                    isOpen={Boolean(isDeliveryTimelinePopoverOpen[delivery_partner?.id ?? dIdx])}
                                                                    positions={["bottom", "left"]}
                                                                    padding={4}
                                                                    onClickOutside={() => toggleDeliveryTimelinePopover(delivery_partner?.id ?? dIdx)}
                                                                    content={
                                                                        <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                                                                            <button
                                                                                className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                                onClick={() => {
                                                                                    if (isOpenViewUploadedPickupProof) {
                                                                                        setIsOpenViewUploadedPickupProof(false);
                                                                                    } else {
                                                                                        setIsOpenViewUploadedPickupProof(delivery_partner?.id);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {t('view_pickup_proof')}
                                                                            </button>
                                                                            <button
                                                                                className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                                onClick={() => {
                                                                                    if (isOpenViewUploadedProof) {
                                                                                        setIsOpenViewUploadedProof(false);
                                                                                    } else {
                                                                                        setIsOpenViewUploadedProof(delivery_partner?.id);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {t('view_uploaded_proof')}
                                                                            </button>
                                                                            {order_details?.delivery_status !== 'cancelled' && <button
                                                                                className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                                onClick={() => {
                                                                                    const vendor = item?.vendors?.[dIdx] ?? null;
                                                                                    openDeliveryTimeline(item, vendor, delivery_partner);
                                                                                    toggleDeliveryTimelinePopover(delivery_partner?.id ?? dIdx);
                                                                                }}
                                                                            >
                                                                                {t('track_delivery_status')}
                                                                            </button>}
                                                                        </div>
                                                                    }
                                                                >
                                                                    <IconButton
                                                                        variant="text"
                                                                        size="sm"
                                                                        onClick={() => toggleDeliveryTimelinePopover(delivery_partner?.id ?? dIdx)}
                                                                    >
                                                                        <EllipsisVerticalIcon className="h-5 w-5 text-gray-600" />
                                                                    </IconButton>
                                                                </Popover>
                                                            )}
                                                            {delivery_partner?.delivery_timeline === 'delivered' &&
                                                                <span className="w-auto min-w-[110px] text-[11px] px-3 py-1 rounded-full text-center font-semibold capitalize bg-green-100 text-green-700 ml-auto">
                                                                    {t('delivered_status')}
                                                                </span>
                                                            }
                                                            {delivery_partner?.status === 'rejected' &&
                                                                <span className="w-auto min-w-[110px] text-[11px] px-3 py-1 rounded-full text-center font-semibold capitalize bg-red-100 text-red-700 ml-auto">
                                                                    {t('rejected_by_dp_status')}
                                                                </span>
                                                            }
                                                        </div>

                                                    ))}
                                                </td>
                                                <td className="py-4 px-4 first:pl-0 last:pr-0">
                                                    <div className="flex items-center gap-2">

                                                        <img src={`/img/icons/logistic-van.png`} alt='logistic-van' className={`h-4 w-4 object-contain ml-4 mt-1`} />
                                                        <Popover
                                                            isOpen={isActionPopoverOpen[item.rfq_product_id]}
                                                            positions={["bottom", "left"]}
                                                            padding={4}
                                                            onClickOutside={() => toggleActionPopover(item.rfq_product_id)}
                                                            content={
                                                                <div className="bg-white shadow-xl rounded-md w-40 flex flex-col z-50 border border-gray-100 py-1">
                                                                    {hasViewVendorPermission && <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize"
                                                                        onClick={() => viewVendor(item)}>{t('view_vendor_button')}</button>}
                                                                </div>
                                                            }
                                                        >
                                                            <IconButton
                                                                variant="text"
                                                                size="sm"
                                                                onClick={() => toggleActionPopover(item.rfq_product_id)}
                                                            >
                                                                <EllipsisVerticalIcon className="h-5 w-5 text-gray-600" />
                                                            </IconButton>
                                                        </Popover>
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className={hasSummaryPermission && order_details ? "grid grid-cols-1 md:grid-cols-[3fr_1fr] gap-4 md:divide-x md:divide-gray-300 w-full" : "w-full"}>
                        <div className={hasSummaryPermission && order_details ? "pr-0 md:pr-4 overflow-x-auto min-h-[400px]" : "w-full"}>
                            {activeTab === "chat" && <OrderChat rfq_id={order_details?.rfq_id} customer_id={order_details?.user_id} purchase_order_file={order_details?.purchase_order_file} purchase_order_number={order_details?.purchase_order_number} />}
                            {activeTab === "customer" && <OrderCustomerInfo rfq_id={order_details?.rfq_id} />}
                            {activeTab === "payments" && <OrderPayments orderId={id} rfq_id={order_details?.rfq_id} />}
                            {activeTab === "refund" && <OrderRefundRequests orderId={id} />}
                            {activeTab === "rfqs" && <OrderRfq rfq_id={order_details?.rfq_id} />}
                            {activeTab === "feedback" && <OrderFeedback rfq_id={rfq_id} />}
                        </div>
                        {hasSummaryPermission && order_details && (
                            <div className="pl-0 md:pl-5">
                                <OrderSummary rfq_id={order_details?.rfq_id} order_id={id} search={search} activeTab={activeTab} />
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isSetPOFile && <DocumentViewModal documentUrl={isSetPOFile} setIsDocumentViewModalOpen={setPOFile}></DocumentViewModal>}

            {openViewModal && (
                <ViewVendorVarientPriceModal
                    onClose={() => {
                        setOpenViewModal(false);
                        // dispatch(fetchOrderItems({ rfq_id: order_details?.rfq_id, search: search, sort_order: filter }));
                    }}
                    item={openViewModal}
                    rfq_id={order_details?.rfq_id}
                />
            )}
            {isMemberShow && <ViewAssignedMembers onClose={() => setIsMemberShow(false)} rfq_id={order_details?.rfq_id} order_id={order_details?.id} />}

            {viewDpContext && (
                <ViewAssignedDeliveryAgentModal
                    onClose={() => setViewDpContext(null)}
                    itemId={viewDpContext.itemId}
                    customer={viewDpContext.customer}
                    rfq_product_id={viewDpContext.rfq_product_id}
                    vendor={viewDpContext.vendor}
                    initialData={viewDpContext.delivery_partner}
                />
            )}

            {isOpenViewUploadedPickupProof && (
                <ViewUploadedPickupProofModal
                    open={isOpenViewUploadedPickupProof ? true : false}
                    onClose={() => setIsOpenViewUploadedPickupProof(false)}
                    deliveryAssignedOrderId={isOpenViewUploadedPickupProof}
                />
            )}

            {deliveryTimelineModalOpen && deliveryTimelineContext && (
                <ViewDeliveryTimelineRouteModal
                    onClose={() => {
                        setDeliveryTimelineModalOpen(false);
                        setDeliveryTimelineContext(null);
                        dispatch(fetchOrderItems({ rfq_id: order_details?.rfq_id, search: search }));
                    }}
                    orderId={deliveryTimelineContext.orderId}
                    vendor={deliveryTimelineContext.vendor}
                    deliveryPartner={deliveryTimelineContext.deliveryPartner}
                    onUpdateStatus={(next) => {
                        // TODO: wire backend API call here when endpoint/thunk is available.
                        // For now, modal updates steps visually via local override.
                        if (next === "delivered") {
                            Toaster.success("Status set to Delivered");
                        }
                    }}
                />
            )}
            {isOpenViewUploadedProof && <ViewUploadedProofModal open={isOpenViewUploadedProof ? true : false} onClose={() => setIsOpenViewUploadedProof(false)} deliveryAssignedOrderId={isOpenViewUploadedProof}></ViewUploadedProofModal>}
        </div>
    );
}

export default CompletedOrderItems;
